import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useUser } from './providers/UserProvider';
import { useShortcutsContext } from './providers/ShortcutsProvider';
import { getPageShortcuts } from './utils/pageShortcuts';
import { shortcutKeyMatches } from './utils/keyboard';
import { AddLinkModal } from './components/AddLinkModal';
import { isModalOpen } from './components/ModalOverlay';
import { PrivateRoute } from './components/PrivateRoute';
import { AdminRoute } from './components/AdminRoute';
import { SuperAdminRoute } from './components/SuperAdminRoute';
// Eagerly imported: everything a logged-out or ordinary reader can land on.
// These belong in the first bundle because making them lazy would trade a
// download that already happened for a round trip on the critical path.
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ReaderPage from './pages/ReaderPage';
import ForYouPage from './pages/ForYouPage';
import ArticleReadPage from './pages/ArticleReadPage';
import StoriesPage from './pages/StoriesPage';
import TuningPage from './pages/TuningPage';
import FeedsPage from './pages/FeedsPage';
import ListsPage from './pages/ListsPage';
import SettingsPage from './pages/SettingsPage';
import SavedSearchesPage from './pages/SavedSearchesPage';
import DigestsPage from './pages/DigestsPage';
import NotFoundPage from './pages/NotFoundPage';
import StoryPermalinkPage from './pages/StoryPermalinkPage';

/*
 * Lazily imported: every page behind AdminRoute or SuperAdminRoute.
 *
 * The whole admin surface used to ship to every reader. One IIFE bundle
 * carried all 46 pages, so somebody arriving at /tldr downloaded the scoring
 * rule editor, the prompt lab and the pipeline console before the feed could
 * render. Splitting at the authorization boundary is the natural cut: a reader
 * who never sees these routes never fetches them, and an admin pays one small
 * chunk fetch on navigation, warm-connection and edge-cached.
 *
 * This is the entire reason the build emits ESM with --splitting. esbuild
 * cannot split an IIFE, and --splitting alone would not have helped either:
 * with one entry point and no dynamic import() in the graph there is nothing
 * to split *at*. These calls are the split points; the flag just lets esbuild
 * honor them.
 *
 * The boundary is load-bearing, so keep it honest. Each of these must be
 * imported here and nowhere else -- a static import of any of them from an
 * eager module silently pulls the page back into the main bundle, with no
 * error and no signal beyond the bundle growing again.
 */
const SitesPage = lazy(() => import('./pages/SitesPage'));
const SiteDetailPage = lazy(() => import('./pages/SiteDetailPage'));
const StoryDetailPage = lazy(() => import('./pages/StoryDetailPage'));
const ChangeDetailPage = lazy(() => import('./pages/ChangeDetailPage'));
const CorrectionPage = lazy(() => import('./pages/CorrectionPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const CostPage = lazy(() => import('./pages/CostPage'));
const CurationDataPage = lazy(() => import('./pages/CurationDataPage'));
const MyActivityPage = lazy(() => import('./pages/MyActivityPage'));
const PromptsPage = lazy(() => import('./pages/PromptsPage'));
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const TopicsPage = lazy(() => import('./pages/TopicsPage'));
const DuplicatesPage = lazy(() => import('./pages/DuplicatesPage'));
const PaywallReviewPage = lazy(() => import('./pages/PaywallReviewPage'));
const ScoringRulesPage = lazy(() => import('./pages/ScoringRulesPage'));
const ScoringRuleDetailPage = lazy(() => import('./pages/ScoringRuleDetailPage'));
const ScoringSettingsPage = lazy(() => import('./pages/ScoringSettingsPage'));
const QualityScoringPage = lazy(() => import('./pages/QualityScoringPage'));
const PromptLabPage = lazy(() => import('./pages/PromptLabPage'));
const PromptLabsHubPage = lazy(() => import('./pages/PromptLabsHubPage'));
const SearchPromptPage = lazy(() => import('./pages/SearchPromptPage'));
const XAccountsPage = lazy(() => import('./pages/XAccountsPage'));
const DirectoryPage = lazy(() => import('./pages/DirectoryPage'));
const SourceScoresPage = lazy(() => import('./pages/SourceScoresPage'));
const RecentDiscoveredFeedsPage = lazy(() => import('./pages/RecentDiscoveredFeedsPage'));
const NewslettersPage = lazy(() => import('./pages/NewslettersPage'));
const AdminHomePage = lazy(() => import('./pages/AdminHomePage'));
const RecentSearchesPage = lazy(() => import('./pages/RecentSearchesPage'));
const SearchExpansionsPage = lazy(() => import('./pages/SearchExpansionsPage'));
const SearchAuditPage = lazy(() => import('./pages/SearchAuditPage'));
import { channelUrl, TLDR_CHANNEL_ID } from './utils/channelUrl';

// The per-kind segments the old search URLs interposed between the prefix and
// the id. They carried no information the page couldn't recover from the id
// itself, so a redirect just drops them.
const LEGACY_SEARCH_KIND_SEGMENTS = new Set(['saved', 'runs', 'descriptions']);

/**
 * Map any superseded search URL onto its canonical `/search/...` form.
 *
 * Handles `/fastsearch/...` in every shape it ever took plus the per-kind
 * `/search/saved|runs|descriptions/...` subpaths, including the `/audit` tail:
 * `/fastsearch/runs/<id>/audit` becomes `/search/<id>/audit`.
 */
function LegacySearchRedirect() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  // Drop the prefix, then the kind segment if one followed it.
  const rest = segments.slice(1);
  if (rest.length && LEGACY_SEARCH_KIND_SEGMENTS.has(rest[0])) rest.shift();
  const target = rest.length ? `/search/${rest.join('/')}` : '/search/new';
  return <Navigate to={`${target}${location.search}`} replace />;
}

function LegacyReaderRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.get('compose') === 'new') {
    return <Navigate to="/search/new" replace />;
  }
  return <Navigate to={channelUrl(params.get('channel') || TLDR_CHANNEL_ID)} replace />;
}

export function App() {
  const { isAdmin, isAdminUser, toggleAdminMode } = useUser();
  const { dynamicGroups } = useShortcutsContext();
  const location = useLocation();
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (shortcutKeyMatches(e, '/') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setShowShortcuts(s => !s); }
      if (shortcutKeyMatches(e, 'h') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = e.target as HTMLElement;
        if (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA' && !t.isContentEditable) {
          e.preventDefault(); setShowShortcuts(s => !s);
        }
      }
      // 'a' toggles admin mode for admins (issue #889). Non-admins keep 'a' as
      // the story expand key, so this listener only claims it for admins.
      if (isAdminUser && shortcutKeyMatches(e, 'a') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = e.target as HTMLElement;
        if (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA' && !t.isContentEditable && !isModalOpen()) {
          e.preventDefault();
          toggleAdminMode();
        }
      }
      if (e.key === 'Escape') setShowShortcuts(false);
    };
    const blur = () => setShowShortcuts(false);
    window.addEventListener('keydown', down);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('blur', blur);
    };
  }, [isAdminUser, toggleAdminMode]);

  const groups = [
    ...getPageShortcuts(location.pathname, {
      isAdmin,
      isAdminUser,
      keyboardShortcutsEnabled: true,
    }),
    ...dynamicGroups,
  ].filter((g) => g.rows.length > 0);

  return (
    <>
    <AddLinkModal isAdmin={isAdmin} />
    {showShortcuts && groups.length > 0 && (
      <div className="shortcuts-overlay">
        {groups.map((group) => (
          <div key={group.heading} className="shortcuts-overlay-section">
            <div className="shortcuts-overlay-heading">{group.heading}</div>
            {group.rows.map((row, i) => (
              <div key={i} className="shortcuts-overlay-row">
                {row.keys.map((k) => <kbd key={k}>{k}</kbd>)}
                {' '}{row.label}
              </div>
            ))}
          </div>
        ))}
      </div>
    )}
    {/*
      * One boundary around the whole table rather than per route: only the
      * lazy admin pages can suspend, and they are mutually exclusive, so a
      * boundary per route would be 31 copies of the same thing.
      *
      * The fallback is deliberately empty. A chunk fetch on a warm connection
      * from a nearby edge is short enough that a spinner would flash in and
      * out and read as a glitch, and the eager reader routes never suspend, so
      * nothing on the critical path is ever waiting behind this.
      */}
    <Suspense fallback={null}>
    <Routes>
      {/* The main feed is the default website and is readable logged out
          (issue #825). Anonymous visitors land on the feed, not a login wall. */}
      <Route path="/" element={<Navigate to="/tldr" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* Linked from backend password-reset emails (app/services/frontend_links.py);
          parity-pinned by backend/tests/test_frontend_route_parity.py. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/tldr" element={<ReaderPage />} />
      {/* Every one of the reader's feeds, merged. Signed-in only: it is built
          from their own saved searches. */}
      <Route path="/for-you" element={<PrivateRoute><ForYouPage /></PrivateRoute>} />
      <Route path="/flagged" element={<PrivateRoute><ReaderPage /></PrivateRoute>} />
      <Route path="/story/:identifier" element={<StoryPermalinkPage />} />
      {/* Visible logged out so anonymous visitors can see what search is;
          running a search still requires an account (ReaderPage gates it). */}
      <Route path="/search/new" element={<ReaderPage />} />
      {/* One id space, one path. A search id may name a saved search, a bare
          description, or a run; ReaderPage already probes all three in turn, so
          the old saved/ descriptions/ runs/ split only made the same page look
          like three. */}
      <Route path="/search/:searchId" element={<PrivateRoute><ReaderPage /></PrivateRoute>} />
      {/* The audit view stays admin-only even though the runs themselves are now
          open to every logged-in user: it exposes cut candidates, per-cover
          verdicts and donor provenance. */}
      <Route path="/search/:searchId/audit" element={<AdminRoute><SearchAuditPage /></AdminRoute>} />
      {/* Superseded URL shapes: the "fastsearch" era, and the per-kind
          subpaths. Redirected, never served, so old bookmarks and pasted links
          keep working. */}
      <Route path="/fastsearch/*" element={<LegacySearchRedirect />} />
      <Route path="/search/saved/*" element={<LegacySearchRedirect />} />
      <Route path="/search/runs/*" element={<LegacySearchRedirect />} />
      <Route path="/search/descriptions/*" element={<LegacySearchRedirect />} />
      <Route path="/reader" element={<LegacyReaderRedirect />} />
      <Route path="/read/:articleId" element={<PrivateRoute><ArticleReadPage /></PrivateRoute>} />
      {/* Tradsearch: usable by any logged-in user (e.g. domain links from the
          feed), but intentionally not surfaced in the nav for non-admins. */}
      <Route path="/search" element={<PrivateRoute><StoriesPage /></PrivateRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminHomePage /></AdminRoute>} />
      <Route path="/admin/directory" element={<AdminRoute><DirectoryPage /></AdminRoute>}>
        <Route index element={<Navigate to="sites" replace />} />
        <Route path="sites" element={<SitesPage hideShell />} />
        <Route path="recent-discoveries" element={<RecentDiscoveredFeedsPage hideShell />} />
        <Route path="articles" element={<Navigate to="/search" replace />} />
        <Route path="x-accounts" element={<XAccountsPage hideShell />} />
        <Route path="users" element={<UsersPage hideShell />} />
        <Route path="source-scores" element={<SourceScoresPage hideShell />} />
      </Route>
      <Route path="/sites" element={<Navigate to="/admin/directory/sites" replace />} />
      <Route path="/sites/:siteId" element={<AdminRoute><SiteDetailPage /></AdminRoute>} />
      <Route path="/admin/articles" element={<Navigate to="/search" replace />} />
      <Route path="/admin/article/:storyId" element={<AdminRoute><StoryDetailPage /></AdminRoute>} />
      <Route path="/admin/change/:changeId" element={<AdminRoute><ChangeDetailPage /></AdminRoute>} />
      <Route path="/admin/correction/:commentId" element={<AdminRoute><CorrectionPage /></AdminRoute>} />
      <Route path="/users" element={<Navigate to="/admin/directory/users" replace />} />
      <Route path="/admin/activity" element={<AdminRoute><ActivityPage /></AdminRoute>} />
      <Route path="/admin/cost" element={<AdminRoute><CostPage /></AdminRoute>} />
      <Route path="/admin/curation-data" element={<AdminRoute><CurationDataPage /></AdminRoute>} />
      <Route path="/admin/newsletters" element={<AdminRoute><NewslettersPage /></AdminRoute>} />
      <Route path="/admin/my-activity" element={<AdminRoute><MyActivityPage /></AdminRoute>} />
      <Route path="/admin/prompts" element={<AdminRoute><PromptsPage /></AdminRoute>} />
      <Route path="/admin/topics/*" element={<AdminRoute><TopicsPage /></AdminRoute>} />
      <Route path="/admin/duplicates/*" element={<AdminRoute><DuplicatesPage /></AdminRoute>} />
      <Route path="/admin/paywall-review" element={<AdminRoute><PaywallReviewPage /></AdminRoute>} />
      <Route path="/admin/pipeline" element={<SuperAdminRoute><PipelinePage /></SuperAdminRoute>} />
      <Route path="/admin/x-accounts" element={<Navigate to="/admin/directory/x-accounts" replace />} />
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
      <Route path="/admin/rules" element={<SuperAdminRoute><ScoringRulesPage /></SuperAdminRoute>} />
      <Route path="/admin/rules/:ruleId" element={<SuperAdminRoute><ScoringRuleDetailPage /></SuperAdminRoute>} />
      <Route path="/admin/scoring-settings" element={<SuperAdminRoute><ScoringSettingsPage /></SuperAdminRoute>} />
      <Route path="/admin/quality-scoring" element={<SuperAdminRoute><QualityScoringPage /></SuperAdminRoute>} />
      <Route path="/admin/prompt-labs" element={<SuperAdminRoute><PromptLabsHubPage /></SuperAdminRoute>} />
      {/* Linked from backend default-prompt alarm emails (app/services/frontend_links.py);
          parity-pinned by backend/tests/test_frontend_route_parity.py. */}
      <Route path="/admin/prompt-lab/:promptType" element={<SuperAdminRoute><PromptLabPage /></SuperAdminRoute>} />
      <Route path="/admin/search-prompt" element={<SuperAdminRoute><SearchPromptPage /></SuperAdminRoute>} />
      <Route path="/admin/search-prompt" element={<Navigate to="/admin/search-prompt" replace />} />
      <Route path="/reader/smartfeed/:ruleId" element={<Navigate to="/tldr" replace />} />
      <Route path="/tuning/:channelId" element={<PrivateRoute><TuningPage /></PrivateRoute>} />
      <Route path="/feeds" element={<PrivateRoute><FeedsPage /></PrivateRoute>} />
      <Route path="/lists" element={<PrivateRoute><ListsPage /></PrivateRoute>} />
      <Route path="/admin/recent-searches" element={<AdminRoute><RecentSearchesPage /></AdminRoute>} />
      <Route path="/admin/search-expansions" element={<AdminRoute><SearchExpansionsPage /></AdminRoute>} />
      <Route path="/saved-searches" element={<PrivateRoute><SavedSearchesPage /></PrivateRoute>} />
      {/* Path pinned to backend frontend_links.DIGESTS_PATH: every digest
          email footer links here. */}
      <Route path="/digests" element={<PrivateRoute><DigestsPage /></PrivateRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    </>
  );
}
